import * as React from 'react';
import {
    ICommand,
    connectToItemCache,
    IItemCache,
    Action,
    IActionProps,
    withSelectedItems,
    withExactlyOneItem,
} from '@ms/items-view';
import { ControlIcons } from 'owa-control-icons';
import { fileKeyFacet } from '../Schema/Facets';
import { getParameterStore } from '../store/ParametersStore';
import { getFilterStore } from '../store/FilterStore';
import { getFilterMenuStore } from '../store/FilterMenuStore';
import { DateRangeOption } from 'owa-files-filter-date-filter';
import { FileExtensionType } from 'owa-files-filter-file-extensions-filter';
import {
    AttachmentModel,
    TypeOfAttachment,
    ReferenceAttachmentModel,
} from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import {
    print,
    isPrintAllowed,
    download,
    isDownloadAllowed,
    openInNewTab,
} from 'owa-attachment-user-operations';
import LinkAttachment from 'owa-service/lib/contract/LinkAttachment';
import { downloadAttachmentsZip } from './downloadAsZip';
import {
    DownloadItemText,
    DownloadItemTooltip,
    DownloadItemAriaLabel,
    PrintItemText,
    PrintItemTooltip,
    PrintItemAriaLabel,
    OpenItemInNewTabText,
    OpenItemInNewTabTooltip,
    OpenItemInNewTabAriaLabel,
    CopyToClipboardText,
    CopyToClipboardTooltip,
    CopyToClipboardAriaLabel,
    ShareByEmailText,
    ShareByEmailTooltip,
    ShareByEmailAriaLabel,
    PreviewItemText,
    PreviewItemTooltip,
    PreviewItemAriaLabel,
} from '../strings.locstring.json';
import loc from 'owa-localize';
import { CopyToClipboardPromptText } from 'owa-locstrings/lib/strings/copytoclipboardprompttext.locstring.json';
import { CopyToClipboardMacPromptText } from 'owa-locstrings/lib/strings/copytoclipboardmacprompttext.locstring.json';
import { isMac } from 'owa-user-agent';
import { shareByEmail } from 'owa-attachment-sharebyemail';
import { ComposeTarget } from 'owa-mail-compose-store';
import { createAttachmentFileFromExistingMailboxAttachment } from 'owa-attachment-well-data/lib/lazyIndex';
import getMaxClassicAttachmentsSize from 'owa-attachment-well-data/lib/utils/getMaxClassicAttachmentsSize';
import { AttachmentFile } from 'owa-attachment-well-data';
import { AttachmentOpenAction, getAttachmentDefaultOpenAction } from 'owa-attachment-data';
import { PreviewPaneSelectionSource, SxSCustomization, sxsStore } from 'owa-sxs-store';
import {
    lazyPreviewAttachmentInSxS,
    SxSReadingPaneStrategy,
    SxSReadingPaneInitializeMethod,
} from 'owa-attachment-preview-sxs-actions';
import { lazySetRightPaneDisplayAndUpdateSize } from 'owa-sxsdata';
import { getAttachmentViewStateStore } from '../store/AttachmentViewStateStore';

const ZIP_FILENAME = 'files';

const PreviewSelectedItemAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
                const { itemKey } = props;

                let isAvailable = false;

                let attachmentId;
                let attachmentViewState;

                let id, conversationId, conversationSubject;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    let fileKey = itemCache.demandItemFacet(fileKeyFacet, itemKey);

                    id = fileKey.id;
                    conversationId = fileKey.conversationId;
                    conversationSubject = fileKey.conversationSubject;

                    attachmentId = {
                        Id: id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };

                    attachmentViewState = getAttachmentViewStateStore().attachmentViewStates.get(
                        attachmentId.Id
                    );

                    isAvailable =
                        getAttachmentDefaultOpenAction(attachmentViewState.strategy) ===
                        AttachmentOpenAction.Preview;
                }

                return {
                    isAvailable: !!isAvailable,
                    onExecute: async () => {
                        let readingPaneStrategy: SxSReadingPaneStrategy = {
                            method: SxSReadingPaneInitializeMethod.AlwaysHidden,
                        };

                        if (conversationId) {
                            readingPaneStrategy = {
                                method: SxSReadingPaneInitializeMethod.LoadConversationReadingPane,
                                conversationId: {
                                    Id: conversationId,
                                    mailboxInfo: parameterStore.mailboxInfo,
                                },
                                conversationSubject: conversationSubject,
                                conversationCategories: [],
                            };
                        }

                        lazySetRightPaneDisplayAndUpdateSize
                            .import()
                            .then(setRightPaneDisplayAndUpdateSize => {
                                setRightPaneDisplayAndUpdateSize(sxsStore.viewState, 1);
                            });

                        lazyPreviewAttachmentInSxS.importAndExecute(
                            attachmentViewState,
                            attachmentId,
                            false,
                            false,
                            PreviewPaneSelectionSource.ConsumerGroupFilesHub,
                            null, // createImageGallery
                            readingPaneStrategy,
                            null, //TODO figure out if I need this (instrumentationContext)
                            SxSCustomization.GroupFilesHub
                        );
                    },
                    engagement: {
                        name: 'Preview',
                        isIntentional: true,
                        scenario: parameterStore.scenarioName,
                    },
                };
            }
        )(Action)
    )
);

const ShareSelectedItemByEmailAction = withSelectedItems()(
    connectToItemCache(
        (
            itemCache: IItemCache,
            props: { itemKeys: string[] }
        ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
            const { itemKeys } = props;

            let isAvailable = false;

            let attachmentId;
            let attachment: AttachmentModel;
            let attachmentFiles: AttachmentFile[] = [];

            const parameterStore = getParameterStore();
            let totalSize = 0;

            for (let i = 0; i < itemKeys.length; i++) {
                attachmentId = {
                    Id: itemCache.demandItemFacet(fileKeyFacet, itemKeys[i]).id,
                    mailboxInfo: parameterStore.mailboxInfo,
                };
                attachment = getAttachment(attachmentId);

                if (attachment.type === TypeOfAttachment.Reference) {
                    totalSize += attachment.model.Size;
                }

                attachmentFiles.push(
                    createAttachmentFileFromExistingMailboxAttachment(attachment.model)
                );
            }

            if (totalSize < getMaxClassicAttachmentsSize()) {
                isAvailable = true;
            }

            return {
                isAvailable: !!isAvailable,
                onExecute: isAvailable
                    ? async () => {
                          shareByEmail(attachmentFiles, ComposeTarget.Popout);
                      }
                    : undefined,
                engagement: {
                    name: 'ShareByEmail',
                    isIntentional: true,
                    scenario: parameterStore.scenarioName,
                },
            };
        }
    )(Action)
);

const CopyToClipboardSelectedItemAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
                const { itemKey } = props;

                let isAvailable = false;

                let attachmentId;
                let attachment: AttachmentModel;

                let url;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    if (attachment.type === TypeOfAttachment.Link) {
                        url = (attachment.model as LinkAttachment).WebUrl;
                    } else if (attachment.type === TypeOfAttachment.Reference) {
                        url = (attachment as ReferenceAttachmentModel).openUrl;
                    }

                    isAvailable = url !== undefined;
                }

                return {
                    isAvailable: !!isAvailable,
                    onExecute: isAvailable
                        ? async () => {
                              const text = isMac()
                                  ? loc(CopyToClipboardMacPromptText)
                                  : loc(CopyToClipboardPromptText);
                              window.prompt(text, url);
                          }
                        : undefined,
                    engagement: {
                        name: 'CopyToClipboard',
                        isIntentional: true,
                        scenario: parameterStore.scenarioName,
                    },
                };
            }
        )(Action)
    )
);

const DownloadSelectedItemsAction = withSelectedItems()(
    connectToItemCache(
        (
            itemCache: IItemCache,
            props: { itemKeys: string[] }
        ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
            const { itemKeys } = props;

            let isAvailable = false;

            let attachmentId;
            let attachmentIds = [];
            let attachment: AttachmentModel;

            const parameterStore = getParameterStore();

            if (itemKeys.length === 1) {
                attachmentId = {
                    Id: itemCache.demandItemFacet(fileKeyFacet, itemKeys[0]).id,
                    mailboxInfo: parameterStore.mailboxInfo,
                };
                attachment = getAttachment(attachmentId);
                isAvailable = isDownloadAllowed(attachment.model);
            } else if (itemKeys.length > 1) {
                isAvailable = true;
                for (let i = 0; i < itemKeys.length; i++) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKeys[i]).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    if (
                        isDownloadAllowed(attachment.model) === false ||
                        attachment.type === TypeOfAttachment.Reference
                    ) {
                        isAvailable = false;
                    }
                    attachmentIds.push(attachmentId.Id);
                }
            }

            return {
                isAvailable: !!isAvailable,
                onExecute: isAvailable
                    ? async () => {
                          if (itemKeys.length === 1) {
                              download(attachmentId, attachment.model);
                          } else if (itemKeys.length > 1) {
                              downloadAttachmentsZip(attachmentIds, ZIP_FILENAME);
                          }
                      }
                    : undefined,
                engagement: {
                    name: 'Download',
                    isIntentional: true,
                    scenario: parameterStore.scenarioName,
                },
            };
        }
    )(Action)
);

const PrintSelectedItemAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
                const { itemKey } = props;

                let isAvailable = false;

                let attachmentId;
                let attachment: AttachmentModel;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    isAvailable = isPrintAllowed(attachment.model);
                }

                return {
                    isAvailable: !!isAvailable,
                    onExecute: isAvailable
                        ? async () => {
                              print(attachmentId, attachment.model);
                          }
                        : undefined,
                    engagement: {
                        name: 'Print',
                        isIntentional: true,
                        scenario: parameterStore.scenarioName,
                    },
                };
            }
        )(Action)
    )
);

const OpenInNewTabSelectedItemAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'engagement' | 'onExecute'> => {
                const { itemKey } = props;

                let attachmentId;
                let attachment: AttachmentModel;
                let webUrl;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    webUrl = (attachment.model as LinkAttachment).WebUrl;
                }

                return {
                    isAvailable: !!webUrl,
                    onExecute: async () => {
                        openInNewTab(webUrl);
                    },
                    engagement: {
                        name: 'OpenInNewTab',
                        isIntentional: true,
                    },
                };
            }
        )(Action)
    )
);

export const previewCommand: ICommand = {
    key: 'preview',
    name: loc(PreviewItemText),
    title: loc(PreviewItemTooltip),
    ariaLabel: loc(PreviewItemAriaLabel),
    iconProps: { iconName: ControlIcons.View },
    action: <PreviewSelectedItemAction />,
};

export const shareByEmailCommand: ICommand = {
    key: 'shareByEmail',
    name: loc(ShareByEmailText),
    title: loc(ShareByEmailTooltip),
    ariaLabel: loc(ShareByEmailAriaLabel),
    iconProps: { iconName: ControlIcons.Mail },
    action: <ShareSelectedItemByEmailAction />,
};

export const copyToClipboardCommand: ICommand = {
    key: 'copyToClipboard',
    name: loc(CopyToClipboardText),
    title: loc(CopyToClipboardTooltip),
    ariaLabel: loc(CopyToClipboardAriaLabel),
    iconProps: { iconName: ControlIcons.Link },
    action: <CopyToClipboardSelectedItemAction />,
};

export const downloadCommand: ICommand = {
    key: 'download',
    name: loc(DownloadItemText),
    title: loc(DownloadItemTooltip),
    ariaLabel: loc(DownloadItemAriaLabel),
    iconProps: { iconName: ControlIcons.Download },
    action: <DownloadSelectedItemsAction />,
};

export const printCommand: ICommand = {
    key: 'print',
    name: loc(PrintItemText),
    title: loc(PrintItemTooltip),
    ariaLabel: loc(PrintItemAriaLabel),
    iconProps: { iconName: ControlIcons.Print },
    action: <PrintSelectedItemAction />,
};

export const openInNewTabCommand: ICommand = {
    key: 'openInNewTab',
    name: loc(OpenItemInNewTabText),
    title: loc(OpenItemInNewTabTooltip),
    ariaLabel: loc(OpenItemInNewTabAriaLabel),
    iconProps: { iconName: ControlIcons.OpenInNewWindow },
    action: <OpenInNewTabSelectedItemAction />,
};

export function isDateRangeOptionSelectedCheck(value: DateRangeOption): boolean {
    return getFilterStore().dateRangeFilter.selectedValue.option === value;
}

export function isExtensionSelected(value: FileExtensionType): boolean {
    return getFilterStore().extensionGroupFilter.selectedValue.has(value.toString());
}

export function isExtensionFilterExpanded(): boolean {
    return getFilterMenuStore().isExtensionFilterExpanded;
}

export function getContextMenuActions() {
    return [downloadCommand, printCommand];
}
